import { PostTypes } from 'constants/postTypes'

import { useMutation } from 'hooks/useMutation'
import { useQueryClient } from 'hooks/useQueryClient'

import {
  createPost as createPostQuery,
  deletePost as deletePostQuery,
  editPost as editPostQuery,
  fetchPost,
} from 'utils/queries/posts'
import { Toast } from 'utils/toast'

import { Event } from 'types/event'
import { InProgressPost, Post } from 'types/post'
import { Data } from 'types/query'

import { useCurrentUser } from './useCurrentUser'
import { useQuery } from './useQuery'

export const usePost = (postId?: string) => {
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()

  const invalidatePostQueries = (postIdToInvalidate?: string) => {
    queryClient.invalidateQueries('posts', { refetchInactive: true })
    postIdToInvalidate &&
      queryClient.invalidateQueries(['post', postIdToInvalidate], { refetchInactive: true })
  }

  const createPost = useMutation<
    unknown,
    { isDraft: boolean; postData: InProgressPost; userId: string }
  >(({ isDraft, postData, userId }) => createPostQuery(userId, postData, isDraft), {
    onSuccess: (data, { postData }) => {
      if (postData && !postData.isDraft) {
        if (postData.type === PostTypes.POST) {
          Toast.success(
            '',
            'Thanks for sharing! Your post is uploading and will be available soon.',
            undefined,
            {},
            true,
            'bottom',
            undefined,
            undefined,
            true,
            '⚡️',
          )
        } else if (postData.type === PostTypes.WORKOUT) {
          Toast.success(
            '',
            `Let's go! Your workout is uploading and will be available in the feed soon.`,
            undefined,
            {},
            true,
            'bottom',
            undefined,
            undefined,
            true,
            '💯',
          )
        }
      }
      invalidatePostQueries()
    },
    onError: () => {
      Toast.error(
        'Unable to create post',
        'Please check the post contains all required information and try again',
      )
    },
  })

  const editPost = useMutation<
    unknown,
    { isDraft: boolean; postData: InProgressPost; userId: string }
  >(
    ({ isDraft, postData, userId }) => {
      return postId
        ? editPostQuery(userId, postId, postData, isDraft)
        : Promise.reject(new Error('Cannot edit post, no postId provided'))
    },
    {
      onSuccess: () => invalidatePostQueries(postId),
      onError: () => {
        Toast.error(
          'Unable to edit post',
          'Please check the post contains all required information and try again',
        )
      },
    },
  )

  const deletePost = useMutation<unknown>(
    () => {
      return postId
        ? deletePostQuery({ id: postId })
        : Promise.reject(new Error('Cannot delete post, no postId provided'))
    },
    {
      onSuccess: () => {
        queryClient.setQueryData(['userCreatedEvents', [currentUser?.id]], (data: Data) => {
          if (data && data.pages) {
            data.pages.forEach((page: Event[]) => {
              const postIndex = page.findIndex((event: Event) => event.objectId === postId)
              if (postIndex > -1) {
                page.splice(postIndex)
              }
            })
          }
          return data
        })

        invalidatePostQueries(postId)
      },
      onError: () => {
        Toast.error('Unable to delete post', 'Please try again later')
      },
    },
  )

  const getPost = useQuery<Post | undefined>(['post', postId], () =>
    postId ? fetchPost({ postId }) : undefined,
  )

  return {
    ...getPost,

    // Mutation Objects
    createPost,
    deletePost,
    editPost,
  }
}
